import React, { Fragment, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Nav, NavItem, NavLink, Card, CardBody, CardHeader, FormGroup, Label, Input } from 'reactstrap';
import { Breadcrumbs, Btn, H5 } from '../../../AbstractElements';
import classnames from 'classnames';
import DataTable from '../../Common/DataTable';
import { Edit } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { formatDateTR } from '../../../_helper/formatters';
import useDebouncedValue from '../../../_helper/useDebouncedValue';
import { appendQueryParamIfMinLength, normalizeApiItems, normalizeSearchFields, normalizeSearchValue } from '../../../_helper/regexHandler';
import { apiClient } from '../../../Api/client';

const Enforcements = () => {
    const navigate = useNavigate();
    
    // useRef to track abort controller for cancelling pending requests
    const abortControllerRef = useRef(null);

    // State for enforcement data
    const [enforcementItems, setEnforcementItems] = useState([]);
    const [enforcementTotalRows, setEnforcementTotalRows] = useState(0);
    const [enforcementPage, setEnforcementPage] = useState(1);
    const [enforcementPageSize, setEnforcementPageSize] = useState(10);
    const [isLoadingEnforcements, setIsLoadingEnforcements] = useState(true);

    // Subtab state
    const [activeEnforcementSubTab, setActiveEnforcementSubTab] = useState('1'); // '1' for İlamsız, '2' for İlamlı ve Sigorta

    // Insurance companies state
    const [insuranceCompanies, setInsuranceCompanies] = useState([]);
    const [isLoadingInsuranceCompanies, setIsLoadingInsuranceCompanies] = useState(false);

    // Filters
    const [enforcementCaseNumberSearch, setEnforcementCaseNumberSearch] = useState('');
    const [customerNameSearch, setCustomerNameSearch] = useState('');
    const [followupDateSearch, setFollowupDateSearch] = useState('');
    const [debtorSearch, setDebtorSearch] = useState('');
    const [insuranceCompanySearch, setInsuranceCompanySearch] = useState('');
    const [trackingDetailTypeSearch, setTrackingDetailTypeSearch] = useState([]);
    const [enforcementStateSearch, setEnforcementStateSearch] = useState('');

    const currentFilters = useMemo(() => ({
        enforcementCaseNumberSearch,
        customerNameSearch,
        followupDateSearch,
        debtorSearch,
        insuranceCompanySearch,
        trackingDetailTypeSearch,
        enforcementStateSearch,
        activeEnforcementSubTab
    }), [
        enforcementCaseNumberSearch,
        customerNameSearch,
        followupDateSearch,
        debtorSearch,
        insuranceCompanySearch,
        trackingDetailTypeSearch,
        enforcementStateSearch,
        activeEnforcementSubTab
    ]);

    const debouncedFilters = useDebouncedValue(currentFilters, 500);
    const currentFiltersKey = useMemo(() => JSON.stringify(currentFilters), [currentFilters]);
    const debouncedFiltersKey = useMemo(() => JSON.stringify(debouncedFilters), [debouncedFilters]);
    const isFiltersDirty = currentFiltersKey !== debouncedFiltersKey;

    useEffect(() => {
        if (isFiltersDirty) {
            setIsLoadingEnforcements(true);
        }
    }, [isFiltersDirty]);

    // Takip Detay Türü seçenekleri
    const trackingDetailTypeOptions = [
        { value: 'HM', label: 'HM' },
        { value: 'DK', label: 'DK' },
        { value: 'LİMİT', label: 'LİMİT' },
        { value: 'ST', label: 'ST' },
        { value: 'AHD', label: 'AHD' },
        { value: 'HSR', label: 'HSR' },
        { value: 'HF', label: 'HF' }
    ];



    // Toggle subtab
    const toggleEnforcementSubTab = subtab => {
        if (activeEnforcementSubTab !== subtab) {
            setIsLoadingEnforcements(true);
            setActiveEnforcementSubTab(subtab);
            setEnforcementPage(1);
            // Reset search filters when switching subtabs
            setEnforcementCaseNumberSearch('');
            setCustomerNameSearch('');
            setFollowupDateSearch('');
            
            // İlamsız sekmesine geçerken sigorta şirketi filtresini temizle
            if (subtab === '1') {
                setInsuranceCompanySearch('');
            }
            // İlamlı ve Sigorta sekmesine geçerken borçlu filtresini temizle
            if (subtab === '2') {
                setDebtorSearch('');
            }
            
            setTrackingDetailTypeSearch([]);
            setEnforcementStateSearch('');
        }
    };

    // Fetch Insurance Companies
    const fetchInsuranceCompanies = useCallback(async () => {
        setIsLoadingInsuranceCompanies(true);
        try {
            const response = await apiClient.get('/insurancecompany', { params: { page: 1, pageSize: 1000 } });
            const items = normalizeApiItems(response.data?.data || response.data);

            if (items.length > 0) {
                setInsuranceCompanies(items);
            } else {
                setInsuranceCompanies([]);
            }
        } catch (error) {
            toast.error('Sigorta şirketleri yüklenirken bir hata oluştu.');
            setInsuranceCompanies([]);
        } finally {
            setIsLoadingInsuranceCompanies(false);
        }
    }, []);

    // Load insurance companies when component mounts
    useEffect(() => {
        fetchInsuranceCompanies();
    }, [fetchInsuranceCompanies]);

    // Fetch Enforcement data
    const fetchEnforcementData = useCallback(async () => {
        if (isFiltersDirty) {
            return;
        }

        setIsLoadingEnforcements(true);
        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        console.log('🚀 fetchEnforcementData CALLED:', {
            enforcementCaseNumberSearch: debouncedFilters.enforcementCaseNumberSearch,
            customerNameSearch: debouncedFilters.customerNameSearch,
            followupDateSearch: debouncedFilters.followupDateSearch,
            debtorSearch: debouncedFilters.debtorSearch,
            insuranceCompanySearch: debouncedFilters.insuranceCompanySearch,
            trackingDetailTypeSearch: debouncedFilters.trackingDetailTypeSearch,
            enforcementStateSearch: debouncedFilters.enforcementStateSearch,
            enforcementPage,
            enforcementPageSize,
            activeEnforcementSubTab: debouncedFilters.activeEnforcementSubTab
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Enforcement';
        if (!apiEndpoint) {
            console.error("API endpoint is not configured for enforcement data.");
            return;
        }

        const searchFields = [
            { key: 'caseNumber', value: debouncedFilters.enforcementCaseNumberSearch, minLength: 3 },
            { key: 'customerName', value: debouncedFilters.customerNameSearch, minLength: 2 }
        ];

        if (debouncedFilters.activeEnforcementSubTab === '1') {
            searchFields.push({ key: 'debtor', value: debouncedFilters.debtorSearch, minLength: 2 });
        }

        const { normalized, hasInvalid } = normalizeSearchFields(searchFields);

        if (hasInvalid) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            setIsLoadingEnforcements(false);
            return;
        }
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('page', enforcementPage.toString());
            queryParams.append('pageSize', enforcementPageSize.toString());

            // Add isSentenced parameter based on active subtab
            const isSentenced = debouncedFilters.activeEnforcementSubTab === '2';
            queryParams.append('isSentenced', isSentenced.toString());

            // İcra dosya numarası filtresi
            appendQueryParamIfMinLength(queryParams, 'enforcementCaseNumber', normalized.caseNumber, 3);

            // Müşteri adı filtresi
            appendQueryParamIfMinLength(queryParams, 'customerName', normalized.customerName, 2);

            // Takip tarihi filtresi
            const normalizedFollowupDate = normalizeSearchValue(debouncedFilters.followupDateSearch);
            if (normalizedFollowupDate.length > 0) {
                queryParams.append('followupdate', normalizedFollowupDate);
            }

            // Borçlu filtresi - sadece İlamsız sekmesinde (subtab '1')
            if (debouncedFilters.activeEnforcementSubTab === '1') {
                appendQueryParamIfMinLength(queryParams, 'debtor', normalized.debtor, 2);
            }

            // Sigorta şirketi filtresi - sadece İlamlı ve Sigorta sekmesinde (subtab '2')
            if (debouncedFilters.activeEnforcementSubTab === '2') {
                const normalizedInsuranceCompany = normalizeSearchValue(debouncedFilters.insuranceCompanySearch);
                if (normalizedInsuranceCompany.length > 0) {
                    queryParams.append('insuranceCompany', normalizedInsuranceCompany);
                }
            }

            // Takip detay türü filtresi - multi-select seçimleri virgülle birleştirilerek tek string olarak gönderilir
            // Örnek: HM,ST veya DK,LİMİT
            if (Array.isArray(debouncedFilters.trackingDetailTypeSearch) && debouncedFilters.trackingDetailTypeSearch.length > 0) {
                const trackingDetailTypeValue = debouncedFilters.trackingDetailTypeSearch.join(',');
                queryParams.append('trackingDetailType', trackingDetailTypeValue);
            }

            // Durum filtresi
            const normalizedState = normalizeSearchValue(debouncedFilters.enforcementStateSearch);
            if (normalizedState.length > 0) {
                queryParams.append('state', normalizedState);
            }

            const response = await apiClient.get(apiEndpoint, { params: Object.fromEntries(queryParams), signal });
            const result = response.data;

            if (signal.aborted) return;

            if (result && result.isSuccess && result.data) {
                const items = result.data.items || [];
                const totalCount = result.data.total || 0;
                setEnforcementItems(items);
                setEnforcementTotalRows(totalCount);
            } else {
                setEnforcementItems([]);
                setEnforcementTotalRows(0);
            }
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
            toast.error('İcra verileri yüklenirken bir hata oluştu.');
            setEnforcementItems([]);
            setEnforcementTotalRows(0);
        } finally {
            if (!signal.aborted) {
                setIsLoadingEnforcements(false);
            }
        }
    }, [
        enforcementPage,
        enforcementPageSize,
        debouncedFilters,
        isFiltersDirty
    ]);

    useEffect(() => {
        fetchEnforcementData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchEnforcementData]);

    // Column definitions
    const enforcementColumns = [
        {
            name: 'Müvekkil Adı / Şirket Adı',
            selector: row => {
                const customer = row?.accidentFile?.customer;
                if (customer) {
                    return customer.companyName || customer.clientName || '-';
                }
                return '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '180px'
        },
        {
            name: 'Plaka',
            selector: row => {
                const licensePlate = row?.accidentFile?.accident?.licensePlate?.licensePlateNumber;
                return licensePlate || '-';
            },
            sortable: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => {
                const accidentDate = row?.accidentFile?.accident?.accidentDate;
                return formatDateTR(accidentDate) || '-';
            },
            sortable: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Takip Tarihi',
            selector: row => {
                const followUpDate = row?.followUpDate;
                return formatDateTR(followUpDate) || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Durum',
            selector: row => row.enforcementStatus || '-',
            sortable: true,
            grow: 1.5,
            minWidth: '120px'
        },
        {
            name: 'İcra Türü',
            selector: row => row.enforcementType || '-',
            sortable: true,
            grow: 1.5,
            minWidth: '140px'
        },
        {
            name: 'İcra Dosya No',
            selector: row => row.enforcementCaseNumber || '-',
            sortable: true,
            grow: 1.5,
            minWidth: '140px'
        },
        {
            name: 'İcra Dairesi',
            selector: row => row.courtOffice || '-',
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Yıl',
            selector: row => row.year || '-',
            sortable: true,
            grow: 0.8,
            minWidth: '80px'
        },
        {
            name: 'Foy No',
            selector: row => row.foyNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Takip Detay Türü',
            selector: row => {
                if (Array.isArray(row.trackingDetailType) && row.trackingDetailType.length > 0) {
                    return row.trackingDetailType.join(', ');
                }
                return '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '160px'
        },
        // Borçlular kolonu - sadece İlamsız sekmesinde gösterilir
        ...(activeEnforcementSubTab === '1' ? [{
            name: 'Borçlular',
            selector: row => {
                if (Array.isArray(row.debtor) && row.debtor.length > 0) {
                    const debtorNames = row.debtor.map(d => d.name).filter(n => n);
                    if (debtorNames.length > 0) {
                        return debtorNames.length > 2 
                            ? debtorNames.slice(0, 2).join(', ') + '...'
                            : debtorNames.join(', ');
                    }
                }
                return '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '180px'
        }] : []),
        // Sigorta Şirketi kolonu - sadece İlamlı veya Sigorta sekmesinde gösterilir
        ...(activeEnforcementSubTab === '2' ? [{
            name: 'Sigorta Şirketi',
            selector: row => row.insuranceCompany?.name || '-',
            sortable: true,
            grow: 2,
            minWidth: '180px'
        }] : []),
        {
            name: 'Toplam Tutar',
            selector: row => {
                if (row.totalAmount) {
                    return new Intl.NumberFormat('tr-TR', {
                        style: 'currency',
                        currency: 'TRY'
                    }).format(row.totalAmount);
                }
                return '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '140px'
        },
        {
            name: 'İşlemler',
            cell: (row) => {
                // Arbitration array'inden ilk ID'yi al
                const arbitrationId = Array.isArray(row.arbitration) && row.arbitration.length > 0 
                    ? row.arbitration[0] 
                    : null;

                if (!arbitrationId) {
                    return <span className="text-muted">-</span>;
                }

                return (
                    <Btn
                        attrBtn={{
                            color: 'primary',
                            size: 'sm',
                            outline: true,
                            onClick: () => navigate(`${process.env.PUBLIC_URL}/arbitration/${arbitrationId}`),
                            title: 'Tahkim Detaylarını Görüntüle'
                        }}
                    >
                        <Edit size={18} />
                    </Btn>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '100px',
            center: true
        }
    ];

    return (
        <Fragment>
            <Breadcrumbs parent="Müşteri Alanları" title="İcralar" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <H5>İcralar</H5>
                                <span>Tanımlanmış olan icra kayıtları aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                {/* Subtabs */}
                                <div className="d-flex justify-content-center mb-4">
                                    <Nav className='nav-primary nav-pills d-flex gap-2'>
                                        <NavItem style={{ width: '200px' }}>
                                            <NavLink
                                                role="button"
                                                tabIndex="0"
                                                className={classnames('d-flex align-items-center justify-content-center px-3 py-2', { 
                                                    'active': activeEnforcementSubTab === '1', 
                                                    'bg-light': activeEnforcementSubTab !== '1', 
                                                    'text-dark': activeEnforcementSubTab !== '1' 
                                                })}
                                                onClick={() => toggleEnforcementSubTab('1')}
                                                style={{ borderRadius: '0.375rem', cursor: 'pointer' }}
                                            >
                                                <span className="fw-semibold">İlamsız</span>
                                            </NavLink>
                                        </NavItem>
                                        <NavItem style={{ width: '200px' }}>
                                            <NavLink
                                                role="button"
                                                tabIndex="0"
                                                className={classnames('d-flex align-items-center justify-content-center px-3 py-2', { 
                                                    'active': activeEnforcementSubTab === '2', 
                                                    'bg-light': activeEnforcementSubTab !== '2', 
                                                    'text-dark': activeEnforcementSubTab !== '2' 
                                                })}
                                                onClick={() => toggleEnforcementSubTab('2')}
                                                style={{ borderRadius: '0.375rem', cursor: 'pointer' }}
                                            >
                                                <span className="fw-semibold">İlamlı veya Sigorta</span>
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </div>

                                {/* Filters */}
                                <Row className="mb-3">
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>İcra Dosya No</Label>
                                            <Input
                                                type="text"
                                                placeholder="İcra dosya no ile ara... (en az 3 karakter)"
                                                value={enforcementCaseNumberSearch}
                                                onChange={(e) => {
                                                    setEnforcementCaseNumberSearch(e.target.value);
                                                    setEnforcementPage(1);
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Müşteri Adı</Label>
                                            <Input
                                                type="text"
                                                placeholder="Müşteri adı ile ara... (en az 2 karakter)"
                                                value={customerNameSearch}
                                                onChange={(e) => {
                                                    setCustomerNameSearch(e.target.value);
                                                    setEnforcementPage(1);
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Takip Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={followupDateSearch}
                                                onChange={(e) => {
                                                    setFollowupDateSearch(e.target.value);
                                                    setEnforcementPage(1);
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {/* Borçlu filtresi sadece İlamsız sekmesinde gösterilir */}
                                    {activeEnforcementSubTab === '1' && (
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Borçlu</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Borçlu adı ile ara... (en az 2 karakter)"
                                                    value={debtorSearch}
                                                    onChange={(e) => {
                                                        setDebtorSearch(e.target.value);
                                                        setEnforcementPage(1);
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                    {/* Sigorta Şirketi filtresi sadece İlamlı ve Sigorta sekmesinde gösterilir */}
                                    {activeEnforcementSubTab === '2' && (
                                        <Col md="3">
                                            <FormGroup>
                                                <Label>Sigorta Şirketi</Label>
                                                <Input
                                                    type="select"
                                                    value={insuranceCompanySearch}
                                                    onChange={(e) => {
                                                        setInsuranceCompanySearch(e.target.value);
                                                        setEnforcementPage(1);
                                                    }}
                                                    disabled={isLoadingInsuranceCompanies}
                                                >
                                                    <option value="">
                                                        {isLoadingInsuranceCompanies ? 'Yükleniyor...' : 'Tümü'}
                                                    </option>
                                                    {insuranceCompanies.map((company) => (
                                                        <option key={company.id} value={company.id}>
                                                            {company.name}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                                <Row className="mb-3">
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Takip Detay Türü</Label>
                                            <Select
                                                isMulti
                                                id="trackingDetailTypeSearch"
                                                name="trackingDetailTypeSearch"
                                                options={trackingDetailTypeOptions}
                                                value={trackingDetailTypeOptions.filter(option => 
                                                    trackingDetailTypeSearch.includes(option.value)
                                                )}
                                                onChange={(selectedOptions) => {
                                                    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                                    setTrackingDetailTypeSearch(values);
                                                    setEnforcementPage(1);
                                                }}
                                                placeholder="Seçiniz..."
                                                classNamePrefix="select"
                                                noOptionsMessage={() => "Seçenek bulunamadı"}
                                                isClearable={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Durum</Label>
                                            <Input
                                                type="select"
                                                value={enforcementStateSearch}
                                                onChange={(e) => {
                                                    setEnforcementStateSearch(e.target.value);
                                                    setEnforcementPage(1);
                                                }}
                                            >
                                                <option value="">Tümü</option>
                                                <option value="Açık">Açık</option>
                                                <option value="Kapalı">Kapalı</option>
                                                <option value="Vazgeçme">Vazgeçme</option>
                                                <option value="Bekleme">Bekleme</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* DataTable */}
                                <DataTable
                                    columns={enforcementColumns}
                                    data={enforcementItems}
                                    stickyActionPosition="left"
                                    pagination
                                    paginationServer
                                    selectableRows={false}
                                    paginationTotalRows={enforcementTotalRows}
                                    onChangePage={(page) => setEnforcementPage(page)}
                                    onChangeRowsPerPage={(newPageSize) => setEnforcementPageSize(newPageSize)}
                                    paginationPerPage={enforcementPageSize}
                                    paginationDefaultPage={enforcementPage}
                                    progressPending={isLoadingEnforcements}
                                    progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                    noDataComponent={
                                        isLoadingEnforcements
                                            ? null
                                            : activeEnforcementSubTab === '1' 
                                                ? "İlamsız icra dosyası bulunamadı" 
                                                : "İlamlı icra dosyası bulunamadı"
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default Enforcements;
